/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Composer, { BaseComposerArgs } from 'cio/components/action-composer/base-glimmer';
import { action } from '@ember/object';
import { InAppPreview } from 'cio/utils/previews';
import { isBlank } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import InAppMessage from 'cio/app-models/in-app/in-app-message';
import { Editor } from 'cio/services/editor';
import { hasInAppPreviewErrors } from 'cio/utils/in-app/preview-error';
import { SafeString } from '@ember/template/-private/handlebars';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { DEFAULT_RECIPIENT, inAppMessageDisplayLabel, RECIPIENT_REQUIRED_ERROR } from 'cio/utils/in-app/message-config';
import { getMessageWithDefaultsForPreview } from 'cio/utils/in-app/preview';
import { TrackedEvents } from 'cio/utils/in-app/events';

const MESSAGE_ID_REQUIRED_ERROR = 'No message selected';

interface ComposerArgs extends BaseComposerArgs {
  preview: InAppPreview;
  hasReadOnlyPermission: boolean;
}

interface ComposerSignature {
  Args: ComposerArgs;
  Element: HTMLDivElement;
}

export default class InApp extends Composer<ComposerSignature> {
  @service analytics!: AnalyticsService;

  @tracked message!: InAppMessage;
  parsedTemplateId?: string;

  previewObserverKeys = ['template.{recipient,bodyJson}'];
  previewURL = 'previews/in_app';

  constructor(owner: unknown, args: ComposerSignature['Args']) {
    super(owner, args);
    this.deserializeMessage();
    this.watchTemplate();
  }

  private setDefaultRecipient() {
    let { template } = this;
    if (isBlank(template.recipient)) {
      template.recipient = DEFAULT_RECIPIENT;
    }
  }

  private deserializeMessage() {
    this.message = InAppMessage.deserialize(this.template.bodyJson);
    this.parsedTemplateId = this.template.id;
  }

  getPreviewData() {
    return {
      to: this.template.recipient,
      body_json: getMessageWithDefaultsForPreview(this.message).serialize(),
      editor: Editor.JSON,
    };
  }

  get recipientErrors() {
    let errors: string[] = [];
    if (this.isRecipientInvalid) {
      errors.push(RECIPIENT_REQUIRED_ERROR);
    }
    return errors;
  }

  get messageErrors() {
    let errors: (string | SafeString)[] = [];
    if (!this.message.isMessageIdPresent) {
      errors.push(MESSAGE_ID_REQUIRED_ERROR);
    } else if (this.message.hasAttributeErrors) {
      errors = [...this.message.attributeErrors];
    }
    return errors;
  }

  get isDirty() {
    return this.template.get('hasDirtyAttributes') || this.message.isDirty;
  }

  get isInvalid() {
    return this.isRecipientInvalid || this.message.hasErrors || this.hasPreviewErrors;
  }

  get isRecipientInvalid() {
    return isBlank(this.template.recipient);
  }

  get hasPreviewErrors() {
    return hasInAppPreviewErrors(this.args.preview);
  }

  @action
  onTemplateChange(id: string) {
    if (id !== this.parsedTemplateId) {
      // template.bodyJson is parsed into an InAppMessage model and kept as the tracked property
      // message. Multilanguage support works by keeping a template for each language so
      // if parsedTemplateId changes, it means another lang tab was selected and message model needs
      // to be updated. Other changes to template are not being tracked because no other source of changes
      // is expected.
      // Why don't parse template into InAppMessage every call?
      // Beyond the computational overhead, the message model is what is being passed to editor and
      // is mutated by changes made by the user. This is a simple way of keep data regardless
      // state and only update template when model is valid.
      this.deserializeMessage();
    }
  }

  @action
  onMessageAttributeChange(name: string, value: string) {
    this.message.setAttribute(name, value);
  }

  @action
  onMessageTemplateChange(messageId: string, attributes: string[]) {
    this.message.changeMessage(messageId, attributes);
  }

  @action
  onMessageConfigChange(config: InAppMessage) {
    let { expiration, priority, display, routeRules, persistent } = config;
    this.message.configure({ expiration, priority, display, routeRules, persistent });
  }

  @action
  onRecipientChange(recipient: string) {
    this.template.recipient = recipient;
  }

  @action
  async save() {
    if (this.isInvalid) {
      return;
    }
    this.prepareSave();
    await this.args.onSave();
    this.deserializeMessage();
    this.trackEditCompleted();
    this.resetDirtyState();
  }

  private prepareSave() {
    let { template } = this;
    template.editor = Editor.JSON;
    template.bodyJson = this.message.serialize();
    this.setDefaultRecipient();
  }

  private resetDirtyState() {
    this.args.onDirtyChange?.(false);
  }

  private trackEditCompleted() {
    let to = this.template.recipient as string;
    let editor = this.template.editor as Editor;

    let { persistent, attributes, expiration, display, priority, routeRules } = this.message;
    let position = inAppMessageDisplayLabel(display);
    this.analytics.track('in_app', TrackedEvents.EditCompleted, {
      to,
      attributes,
      expiration,
      position,
      priority,
      routeRules,
      persistent,
      editor,
    });
  }

  @action
  trackEditMessageClicked() {
    this.analytics.track('in_app', TrackedEvents.EditMessageClicked);
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
    this.deserializeMessage();
    this.resetDirtyState();
    this.args.onRollback();
  }

  @action
  onDirtyChange(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onDirtyChange?.(isDirty);
  }
}
